import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/lib/errors';
import { apiGet } from '@/lib/http/client';
import {
  portfolioOrgInvitationsUrl,
  portfolioQueryKeys,
} from '@/lib/portfolio/api';
import type { PortfolioInvitation } from '@/lib/portfolio/types';

export function useOrgInvitations(portfolioId: number, orgId: number) {
  return useQuery<PortfolioInvitation[]>({
    queryKey: portfolioQueryKeys.orgInvitations(portfolioId, orgId),
    queryFn: async () => {
      try {
        return await apiGet<PortfolioInvitation[]>(
          portfolioOrgInvitationsUrl(portfolioId, orgId)
        );
      } catch (error) {
        // A gated (403) or missing (404) invitations endpoint means this org simply
        // has no invitations to show: access is enforced server-side, and the plan
        // treats that denial as "no invitations". Any other failure — 5xx, 401 auth
        // expiry, network — is a real error and must not be masked as an empty list,
        // so let it propagate to React Query's error state.
        if (
          error instanceof ApiError &&
          (error.isForbidden() || error.isNotFound())
        ) {
          return [];
        }
        throw error;
      }
    },
    enabled: Number.isFinite(portfolioId) && Number.isFinite(orgId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}
